/**
 * Shared safety-surface manifest schema for contributed manuscript demos.
 *
 * These manifests are intentionally data-only. They let each demo expose the
 * same audit surface — safety guarantees, adversarial scenarios, gates, and
 * proof references — without replacing the richer custom panels in each app.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { z } from "zod";

const nonEmptyString = z
  .string()
  .refine((value) => value.trim().length > 0, "must be a non-empty string");

export const CONTRIB_DOCTRINE_GATE_STATUSES = [
  "structural",
  "contained",
  "inert",
  "diagnostic",
] as const;

export const CONTRIB_DOCTRINE_MECHANIZATION_STATUSES = [
  "complete",
  "reference",
  "planned",
  "prose",
] as const;

const ssmReferenceSchema = z
  .object({
    code: nonEmptyString,
    name: nonEmptyString,
    role: nonEmptyString.optional(),
  })
  .strict();

const themeSchema = z
  .object({
    void: nonEmptyString,
    surface: nonEmptyString,
    border: nonEmptyString,
    muted: nonEmptyString,
    text: nonEmptyString,
    bright: nonEmptyString,
    accent: nonEmptyString,
    mono: nonEmptyString,
    sans: nonEmptyString,
  })
  .strict();

const metricSchema = z
  .object({
    label: nonEmptyString,
    value: nonEmptyString,
    detail: nonEmptyString,
    color: nonEmptyString.optional(),
  })
  .strict();

const guaranteeSchema = z
  .object({
    id: nonEmptyString,
    security: nonEmptyString,
    governance: nonEmptyString,
    mathematical: nonEmptyString,
    statement: nonEmptyString,
    proofPath: nonEmptyString,
  })
  .strict();

const scenarioSchema = z
  .object({
    id: nonEmptyString,
    title: nonEmptyString,
    adversaryMove: nonEmptyString,
    missingStructure: nonEmptyString,
    governedWitness: nonEmptyString,
    safeOutcome: nonEmptyString,
    receipt: nonEmptyString,
  })
  .strict();

const gateSchema = z
  .object({
    id: nonEmptyString,
    label: nonEmptyString,
    invariant: nonEmptyString,
    failureReceipt: nonEmptyString,
    status: z.enum(CONTRIB_DOCTRINE_GATE_STATUSES),
  })
  .strict();

const mechanizationSchema = z
  .object({
    label: nonEmptyString,
    path: nonEmptyString,
    status: z.enum(CONTRIB_DOCTRINE_MECHANIZATION_STATUSES),
    note: nonEmptyString,
  })
  .strict();

function addDuplicateFieldIssues<TItem extends object>(
  items: readonly TItem[],
  collectionName: string,
  fieldName: keyof TItem & string,
  context: z.RefinementCtx,
): void {
  const seen = new Map<string, number>();
  items.forEach((item, index) => {
    const rawFieldValue = item[fieldName];
    if (typeof rawFieldValue !== "string") return;
    const fieldValue = rawFieldValue;
    const firstIndex = seen.get(fieldValue);
    if (firstIndex !== undefined) {
      context.addIssue({
        code: "custom",
        path: [collectionName, index, fieldName],
        message: `duplicates ${collectionName}[${firstIndex}].${fieldName} '${fieldValue}'`,
      });
      return;
    }
    seen.set(fieldValue, index);
  });
}

export const contribDoctrineManifestSchema = z
  .object({
    $comment: nonEmptyString.optional(),
    title: nonEmptyString,
    appLabel: nonEmptyString,
    eyebrow: nonEmptyString,
    ssmCoverage: z.array(ssmReferenceSchema).min(1, "must contain at least one entry"),
    tagline: nonEmptyString,
    thesis: nonEmptyString,
    companionPath: nonEmptyString,
    currentScript: nonEmptyString,
    mainRegionId: nonEmptyString,
    rootDataName: nonEmptyString,
    theme: themeSchema,
    metrics: z.array(metricSchema).min(1, "must contain at least one entry"),
    guarantees: z.array(guaranteeSchema).min(1, "must contain at least one entry"),
    scenarios: z.array(scenarioSchema).min(1, "must contain at least one entry"),
    gates: z.array(gateSchema).min(1, "must contain at least one entry"),
    mechanization: z.array(mechanizationSchema).min(1, "must contain at least one entry"),
  })
  .strict()
  .superRefine((manifest, context) => {
    addDuplicateFieldIssues(manifest.ssmCoverage, "ssmCoverage", "code", context);
    addDuplicateFieldIssues(manifest.guarantees, "guarantees", "id", context);
    addDuplicateFieldIssues(manifest.scenarios, "scenarios", "id", context);
    addDuplicateFieldIssues(manifest.gates, "gates", "id", context);
  });

export type ContribDoctrineGateStatus = z.infer<typeof gateSchema>["status"];
export type ContribDoctrineMechanizationStatus = z.infer<typeof mechanizationSchema>["status"];
export type ContribDoctrineSsmReference = z.infer<typeof ssmReferenceSchema>;
export type ContribDoctrineTheme = z.infer<typeof themeSchema>;
export type ContribDoctrineMetric = z.infer<typeof metricSchema>;
export type ContribDoctrineGuarantee = z.infer<typeof guaranteeSchema>;
export type ContribDoctrineScenario = z.infer<typeof scenarioSchema>;
export type ContribDoctrineGate = z.infer<typeof gateSchema>;
export type ContribDoctrineMechanization = z.infer<typeof mechanizationSchema>;
export type ContribDoctrineManifest = z.infer<typeof contribDoctrineManifestSchema>;

export interface ContribDoctrineSummary {
  readonly metricCount: number;
  readonly guaranteeCount: number;
  readonly scenarioCount: number;
  readonly gateCount: number;
  readonly mechanizationCount: number;
  readonly ssmCodes: readonly string[];
  readonly proofPaths: readonly string[];
  readonly receipts: readonly string[];
}

function formatIssuePath(path: readonly PropertyKey[]): string {
  if (path.length === 0) return "manifest";
  return path.reduce<string>((result, part) => {
    if (typeof part === "number") return `${result}[${String(part)}]`;
    return result.length === 0 ? String(part) : `${result}.${String(part)}`;
  }, "manifest");
}

export function validateContribDoctrineManifest(value: unknown): readonly string[] {
  const result = contribDoctrineManifestSchema.safeParse(value);
  if (result.success) return [];
  return result.error.issues.map((issue) => `${formatIssuePath(issue.path)} ${issue.message}`);
}

export function isContribDoctrineManifest(value: unknown): value is ContribDoctrineManifest {
  return contribDoctrineManifestSchema.safeParse(value).success;
}

export function assertContribDoctrineManifest(
  value: unknown,
): asserts value is ContribDoctrineManifest {
  contribDoctrineManifestSchema.parse(value);
}

export function parseContribDoctrineManifest(value: unknown): ContribDoctrineManifest {
  return contribDoctrineManifestSchema.parse(value);
}

export function summarizeContribDoctrineManifest(
  manifest: ContribDoctrineManifest,
): ContribDoctrineSummary {
  const receipts = new Set<string>();
  for (const scenario of manifest.scenarios) {
    receipts.add(scenario.receipt);
  }
  for (const gate of manifest.gates) {
    receipts.add(gate.failureReceipt);
  }

  return {
    metricCount: manifest.metrics.length,
    guaranteeCount: manifest.guarantees.length,
    scenarioCount: manifest.scenarios.length,
    gateCount: manifest.gates.length,
    mechanizationCount: manifest.mechanization.length,
    ssmCodes: manifest.ssmCoverage.map((ssm) => ssm.code),
    proofPaths: manifest.guarantees.map((guarantee) => guarantee.proofPath),
    receipts: [...receipts],
  };
}
